import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { Copy, MoreHorizontal, Pencil, Plus, Trash } from 'lucide-react';
import { getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable, } from '@tanstack/react-table';
import { Button, Card, CardContent, DataTable, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, TablePagination, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, } from '@cargomatic/ui';
import Container from 'components/v2/container';
import { ConfirmationModal } from 'components/v2/confirmation-modal';
import { FilterCardHeader } from 'components/design-system/molecules/FilterCardHeader';
import { FilterBarRegistry } from 'components/design-system/molecules/FilterBarRegistry';
import { initialValuesFromConfigs, useFilterState, } from 'components/design-system/hooks/useFilterState';
import SessionStore from 'stores/SessionStore';
import Store from 'stores/TariffsStore';
import { getTariffs, getTariffsUsage, removeTariff, } from 'utils/TariffAPIUtils';
import { TariffEditorDialog } from './TariffEditorDialog';
import { makeTariffsFilters } from './Tariffs.filters';
import { getTariffsColumns } from './TariffsColumns';
const hasTariffRole = () => SessionStore.getUserRoles().indexOf('tariff') > -1 ||
    SessionStore.getUserRoles().indexOf('admin') > -1;
function getStateFromStore() {
    const all = Store.getTariffs() || [];
    const usage = Store.getUsage() || {};
    return all.map((t) => (Object.assign(Object.assign({}, t), { numberOfShippers: usage[t._id] || 0 })));
}
export function Tariffs() {
    const { t } = useTranslation();
    const [tariffs, setTariffs] = useState([]);
    const [editorOpen, setEditorOpen] = useState(false);
    const [editingTariff, setEditingTariff] = useState(null);
    const [deletingTariff, setDeletingTariff] = useState(null);
    useEffect(() => {
        document.title = 'Cargomatic Inc. - Tariffs';
    }, []);
    useEffect(() => {
        const onChange = () => setTariffs(getStateFromStore());
        Store.addChangeListener(onChange);
        getTariffsUsage();
        getTariffs();
        return () => Store.removeChangeListener(onChange);
    }, []);
    // --- Derive market options from the loaded tariffs ----------------------
    const marketOptions = useMemo(() => {
        const set = new Set();
        for (const tariff of tariffs) {
            for (const m of tariff.markets || [])
                set.add(m);
        }
        return [...set].sort();
    }, [tariffs]);
    // --- Filter state via design-system hook --------------------------------
    const FILTERS = useMemo(() => makeTariffsFilters({ markets: marketOptions }), [marketOptions]);
    const initialValues = useMemo(() => initialValuesFromConfigs(FILTERS), [FILTERS]);
    const { values, displayed, labels, setValue, setMany, setLabel, toggle } = useFilterState(initialValues);
    // --- Apply filters client-side ------------------------------------------
    const filteredTariffs = useMemo(() => {
        const v = values;
        const nameQ = (v.name || '').trim().toLowerCase();
        const types = v.type || [];
        const markets = v.markets || [];
        const defaultOnly = !!v.is_default;
        return tariffs.filter((tariff) => {
            if (nameQ && !(tariff.name || '').toLowerCase().includes(nameQ))
                return false;
            if (types.length && !types.includes(tariff.type))
                return false;
            if (markets.length &&
                !(tariff.markets || []).some((m) => markets.includes(m)))
                return false;
            if (defaultOnly && !tariff.is_default)
                return false;
            return true;
        });
    }, [tariffs, values]);
    // --- Filter handlers ----------------------------------------------------
    const onChangeFilter = useCallback((id, value) => {
        setValue(id, value);
    }, [setValue]);
    const onToggleFilter = useCallback((id) => {
        toggle(id);
    }, [toggle]);
    const onReset = useCallback(() => {
        const cleared = {};
        for (const cfg of FILTERS) {
            cleared[cfg.id] =
                cfg.kind === 'select-multi' || cfg.kind === 'autocomplete-multi'
                    ? []
                    : cfg.kind === 'toggle'
                        ? false
                        : null;
        }
        setMany(cleared);
    }, [FILTERS, setMany]);
    // --- Row actions --------------------------------------------------------
    const handleDelete = useCallback(() => {
        if (!deletingTariff)
            return;
        Store.reset();
        removeTariff(deletingTariff, '');
        setDeletingTariff(null);
    }, [deletingTariff]);
    const handleEdit = useCallback((tariff) => {
        setEditingTariff(tariff);
        setEditorOpen(true);
    }, []);
    const handleClone = useCallback((tariff) => {
        const copy = JSON.parse(JSON.stringify(tariff));
        delete copy._id;
        copy.name = `${copy.name}_copy`;
        setEditingTariff(copy);
        setEditorOpen(true);
    }, []);
    const handleNew = useCallback(() => {
        setEditingTariff(null);
        setEditorOpen(true);
    }, []);
    const handleCloseEditor = useCallback(() => {
        setEditorOpen(false);
        setEditingTariff(null);
    }, []);
    // --- Columns (data + appended Actions, matches AccessorialRatesTable) ---
    const baseColumns = useMemo(() => getTariffsColumns({ t }), [t]);
    const columns = useMemo(() => {
        const canEdit = hasTariffRole();
        const deferred = (fn) => () => requestAnimationFrame(fn);
        return [
            ...baseColumns,
            {
                id: 'actions',
                header: () => null,
                cell: ({ row }) => {
                    if (!canEdit) {
                        return (React.createElement("span", { className: "text-muted-foreground" }, t('ACTIONS_UNAVAILABLE')));
                    }
                    return (React.createElement("div", { className: "text-right" },
                        React.createElement(DropdownMenu, { modal: false },
                            React.createElement(DropdownMenuTrigger, { asChild: true },
                                React.createElement(Button, { variant: "ghost", className: "w-8 h-8 p-0" },
                                    React.createElement("span", { className: "sr-only" }, t('OPEN_MENU')),
                                    React.createElement(MoreHorizontal, { className: "w-4 h-4" }))),
                            React.createElement(DropdownMenuContent, { align: "end" },
                                React.createElement(DropdownMenuLabel, null, t('ACTIONS')),
                                React.createElement(DropdownMenuItem, { onClick: deferred(() => handleEdit(row.original)) },
                                    React.createElement(Pencil, { className: "h-4 w-4 mr-2" }),
                                    t('EDIT')),
                                React.createElement(DropdownMenuItem, { onClick: deferred(() => handleClone(row.original)) },
                                    React.createElement(Copy, { className: "h-4 w-4 mr-2" }),
                                    titleize(t('CLONE'))),
                                React.createElement(DropdownMenuSeparator, null),
                                row.original.is_default ? (React.createElement(TooltipProvider, null,
                                    React.createElement(Tooltip, null,
                                        React.createElement(TooltipTrigger, { asChild: true },
                                            React.createElement("span", null,
                                                React.createElement(DropdownMenuItem, { disabled: true, className: "text-destructive focus:text-destructive" },
                                                    React.createElement(Trash, { className: "h-4 w-4 mr-2" }),
                                                    titleize(t('REMOVE'))))),
                                        React.createElement(TooltipContent, { side: "left" }, t('CANNOT_REMOVE_DEFAULT_TARIFF', {
                                            defaultValue: 'Cannot remove a default tariff. Unset default first.',
                                        }))))) : (React.createElement(DropdownMenuItem, { onClick: deferred(() => setDeletingTariff(row.original)), className: "text-destructive focus:text-destructive" },
                                    React.createElement(Trash, { className: "h-4 w-4 mr-2" }),
                                    titleize(t('REMOVE'))))))));
                },
                enableSorting: false,
                size: 60,
            },
        ];
    }, [baseColumns, handleEdit, handleClone, t]);
    // --- Tanstack table (client-side sort + paginate) -----------------------
    const [sorting, setSorting] = useState([]);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 20,
    });
    useEffect(() => {
        setPagination((prev) => {
            const maxPageIndex = Math.max(0, Math.ceil(filteredTariffs.length / prev.pageSize) - 1);
            return prev.pageIndex > maxPageIndex
                ? Object.assign(Object.assign({}, prev), { pageIndex: maxPageIndex }) : prev;
        });
    }, [filteredTariffs.length]);
    const table = useReactTable({
        columns,
        data: filteredTariffs,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        state: { sorting, pagination },
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
    });
    return (React.createElement(Container, { className: "py-6", useMaxWidth: false, dataTestId: "tariffs" },
        React.createElement(Card, null,
            React.createElement(FilterCardHeader, { title: t('TARIFFS'), onReset: onReset, actions: hasTariffRole() ? (React.createElement(Button, { size: "sm", onClick: handleNew },
                    React.createElement(Plus, { className: "h-4 w-4 mr-1" }),
                    titleize(t('ADD')))) : null },
                React.createElement(FilterBarRegistry, { configs: FILTERS, values: values, labels: labels, displayed: displayed, onChange: onChangeFilter, onLabelChange: (id, optionId, label) => setLabel(id, optionId, label), onToggle: onToggleFilter })),
            React.createElement(CardContent, null,
                React.createElement(DataTable, { table: table }),
                filteredTariffs.length > 0 && (React.createElement(TablePagination, { page: pagination.pageIndex + 1, pages: table.getPageCount(), total: filteredTariffs.length, perPage: pagination.pageSize, onPageChange: (p) => table.setPageIndex(p - 1), pageSizeOptions: [10, 20, 50, 100], onPageSizeChange: (size) => table.setPageSize(size) })))),
        editorOpen && (React.createElement(TariffEditorDialog, { tariff: editingTariff, tariffsList: tariffs, onClose: handleCloseEditor })),
        React.createElement(ConfirmationModal, { open: !!deletingTariff, title: t('WARNING'), description: t('IMPACT_CUSTOMERS_MSG'), onHide: () => setDeletingTariff(null), onConfirm: handleDelete, confirmText: t('CONFIRM_REMOVE'), cancelText: titleize(t('CANCEL')) })));
}
export default Tariffs;
